import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  MessageCircle, Send, Loader2, Phone, RefreshCw, AlertCircle, Plus, Search,
  CheckCircle2, Link2, X, ChevronLeft,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useRealtime, useDesktopNotifications } from '../hooks/useRealtime';
import { useFieldDraft } from '../hooks/useFieldDraft';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatPhoneDisplay(e164) {
  if (!e164) return '';
  const m = String(e164).match(/^\+1?(\d{3})(\d{3})(\d{4})$/);
  if (m) return `(${m[1]}) ${m[2]}-${m[3]}`;
  return e164;
}

function timeAgo(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return d.toLocaleDateString();
  } catch { return ''; }
}

function fullTimestamp(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

/**
 * SmsInbox — Phase 7 of Communications. Two-way SMS via Twilio.
 *
 * Layout: numbers/setup banner → conversation list (left) → message thread (right) + composer.
 */
export default function SmsInbox() {
  const [config, setConfig] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [loadingConvs, setLoadingConvs] = useState(false);
  const [selected, setSelected] = useState(null);  // { our_number, their_number, ... }
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeFrom, setComposeFrom] = useState('');
  const [composeTo, setComposeTo] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [savingWebhook, setSavingWebhook] = useState(null);

  // Preserve in-progress SMS reply and new-conversation body across timeouts.
  const replyDraftKey = selected
    ? `sms-reply:${selected.our_number}:${selected.their_number}`
    : null;
  const { clearDraft: clearReplyDraft } = useFieldDraft(replyDraftKey, draft, setDraft);
  const { clearDraft: clearComposeBodyDraft } = useFieldDraft(
    composeOpen ? 'sms-compose:new' : null,
    composeBody,
    setComposeBody
  );
  const pollRef = useRef(null);
  const endRef = useRef(null);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sms/config`);
      setConfig(res.data);
      if (res.data.numbers?.length > 0 && !composeFrom) {
        setComposeFrom(res.data.numbers[0].phone_number);
      }
    } catch (e) {
      console.error('Failed to load SMS config:', e);
      const detail = e.response?.data?.detail || e.message;
      toast.error(`SMS configuration error: ${detail}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchConversations = useCallback(async () => {
    setLoadingConvs(true);
    try {
      const res = await axios.get(`${API}/sms/conversations`);
      setConversations(res.data.conversations || []);
    } catch (e) {
      console.error('Failed to load conversations:', e);
    } finally {
      setLoadingConvs(false);
    }
  }, []);

  const fetchMessages = useCallback(async (conv) => {
    if (!conv) return;
    setLoadingMsgs(true);
    try {
      const res = await axios.get(
        `${API}/sms/conversations/messages?our_number=${encodeURIComponent(conv.our_number)}&their_number=${encodeURIComponent(conv.their_number)}`
      );
      setMessages(res.data.messages || []);
      // Mark read
      if (conv.unread > 0) {
        try {
          await axios.post(`${API}/sms/conversations/mark-read`, {
            our_number: conv.our_number,
            their_number: conv.their_number,
          });
          // Reflect locally
          setConversations(prev => prev.map(c =>
            c.our_number === conv.our_number && c.their_number === conv.their_number
              ? { ...c, unread: 0 }
              : c
          ));
        } catch {}
      }
    } catch (e) {
      toast.error(`Failed to load messages: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoadingMsgs(false);
    }
  }, []);

  useEffect(() => { fetchConfig(); fetchConversations(); }, [fetchConfig, fetchConversations]);

  // Poll every 30s as a fallback for missed events (realtime push covers the common case)
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      fetchConversations();
      if (selected) fetchMessages(selected);
    }, 30000);
    return () => clearInterval(pollRef.current);
  }, [fetchConversations, fetchMessages, selected]);

  // Realtime: refresh on inbound SMS event
  const { notify } = useDesktopNotifications();
  useRealtime({
    'sms.received': (data) => {
      // Always refresh the conversation list so the unread badge and latest
      // message preview update immediately.
      fetchConversations();
      // If the conversation that received the message is currently open,
      // append the new message to the thread view.
      if (selected
          && data?.from_number === selected.their_number
          && data?.to_number === selected.our_number) {
        fetchMessages(selected);
      }
      // Desktop notification when the user is on a different tab
      const who = data?.contact?.name || data?.from_number || 'New text';
      notify(`SMS from ${who}`, {
        body: (data?.body || '').slice(0, 120),
        onClick: () => {
          // Open the relevant conversation
          setSelected({
            our_number: data.to_number,
            their_number: data.from_number,
            unread: 0,
            contact: data.contact,
          });
        },
      });
    },
  });

  // Auto-scroll to newest message on thread change / new messages
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, selected]);

  const openConversation = (conv) => {
    setSelected(conv);
    setDraft('');
    fetchMessages(conv);
  };

  const sendReply = async () => {
    if (!selected || !draft.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/sms/send`, {
        from_number: selected.our_number,
        to_number: selected.their_number,
        body: draft,
      });
      setDraft('');
      clearReplyDraft();
      // Immediately refresh
      await fetchMessages(selected);
      await fetchConversations();
    } catch (e) {
      toast.error(`Send failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSending(false);
    }
  };

  const startNewConversation = async () => {
    if (!composeFrom || !composeTo.trim() || !composeBody.trim()) {
      toast.error('From, To, and Message are all required');
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/sms/send`, {
        from_number: composeFrom,
        to_number: composeTo,
        body: composeBody,
      });
      toast.success('SMS sent');
      setComposeOpen(false);
      setComposeTo('');
      setComposeBody('');
      clearComposeBodyDraft();
      await fetchConversations();
      // Open the new conversation
      const fresh = { our_number: res.data.from_number, their_number: res.data.to_number, unread: 0 };
      openConversation(fresh);
    } catch (e) {
      toast.error(`Send failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSending(false);
    }
  };

  const configureWebhook = async (numberSid) => {
    if (!window.confirm('Point this Twilio number\'s SMS webhook at this app so inbound texts appear in the Communications tab?')) return;
    setSavingWebhook(numberSid);
    try {
      await axios.post(`${API}/sms/numbers/${numberSid}/configure-webhook`);
      toast.success('Webhook configured — inbound texts will now appear here.');
      await fetchConfig();
    } catch (e) {
      toast.error(`Failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSavingWebhook(null);
    }
  };

  // ------- Render -------

  if (!config) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!config.configured) {
    return (
      <div className="p-8 text-center" data-testid="sms-not-configured">
        <AlertCircle size={32} className="mx-auto text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground">
          Twilio is not configured. Ask an administrator to set up the integration.
        </p>
      </div>
    );
  }

  const numbers = config.numbers || [];
  const expectedWebhook = config.expected_webhook_url;

  return (
    <div className="space-y-3" data-testid="sms-inbox">
      {/* Webhook setup banner — only shown if any number's webhook isn't pointed at us */}
      {numbers.some(n => n.sms_webhook !== expectedWebhook) && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="webhook-setup-banner">
          <div className="flex items-start gap-2 mb-2">
            <AlertCircle size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-amber-900">Inbound SMS not yet routed</p>
              <p className="text-xs text-amber-800 mt-0.5">
                Click "Connect" next to a number to point its Twilio SMS webhook at this app.
                After that, every text sent to that number lands here.
              </p>
            </div>
          </div>
          <div className="space-y-1.5">
            {numbers.map(n => {
              const connected = n.sms_webhook === expectedWebhook;
              return (
                <div key={n.sid} className="flex items-center justify-between gap-2 bg-white border rounded-md px-3 py-2">
                  <div className="min-w-0">
                    <p className="font-mono text-sm">{formatPhoneDisplay(n.phone_number)}</p>
                    <p className="text-[11px] text-muted-foreground truncate">
                      Webhook: <span className="font-mono">{n.sms_webhook || '(none)'}</span>
                    </p>
                  </div>
                  {connected ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold">
                      <CheckCircle2 size={12} /> Connected
                    </span>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => configureWebhook(n.sid)}
                      disabled={savingWebhook === n.sid}
                      data-testid={`configure-webhook-${n.sid}`}
                    >
                      <Link2 size={14} className="mr-1" />
                      {savingWebhook === n.sid ? 'Saving…' : 'Connect'}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Main two-pane layout */}
      <div className="grid grid-cols-1 md:grid-cols-[minmax(280px,360px)_1fr] gap-3 h-[calc(100vh-280px)] min-h-[500px]">
        {/* Conversations list */}
        <section className="bg-white border rounded-lg overflow-hidden flex flex-col">
          <div className="p-3 border-b flex items-center justify-between">
            <h3 className="text-sm font-semibold">Conversations</h3>
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" onClick={fetchConversations} className="h-7 w-7 p-0" data-testid="refresh-conversations">
                <RefreshCw size={14} className={loadingConvs ? 'animate-spin' : ''} />
              </Button>
              <Button size="sm" onClick={() => setComposeOpen(true)} className="h-7 px-2 gap-1" data-testid="new-sms-btn">
                <Plus size={14} /> New
              </Button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loadingConvs && conversations.length === 0 ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="animate-spin text-muted-foreground" />
              </div>
            ) : conversations.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-muted-foreground text-sm gap-2 p-4 text-center">
                <MessageCircle size={24} />
                <p>No SMS conversations yet</p>
                <p className="text-xs">Click "New" to send your first text.</p>
              </div>
            ) : conversations.map(conv => {
              const active = selected && selected.our_number === conv.our_number && selected.their_number === conv.their_number;
              return (
                <button
                  key={`${conv.our_number}_${conv.their_number}`}
                  onClick={() => openConversation(conv)}
                  data-testid={`sms-conv-${conv.their_number}`}
                  className={`w-full text-left px-3 py-2.5 border-b transition-colors ${
                    active ? 'bg-primary/10' : 'hover:bg-muted/50'
                  } ${conv.unread > 0 ? 'bg-white' : 'bg-muted/20'}`}
                >
                  <div className="flex items-start gap-2">
                    <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${conv.unread > 0 ? 'bg-primary' : 'bg-transparent'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className={`text-sm truncate ${conv.unread > 0 ? 'font-semibold' : ''}`}>
                          {conv.contact?.name || formatPhoneDisplay(conv.their_number)}
                        </span>
                        <span className="text-xs text-muted-foreground flex-shrink-0">{timeAgo(conv.last_message_at)}</span>
                      </div>
                      {conv.contact && (
                        <p className="text-[11px] text-muted-foreground capitalize">
                          {conv.contact.type} · {formatPhoneDisplay(conv.their_number)}
                        </p>
                      )}
                      <p className={`text-xs truncate mt-0.5 ${conv.unread > 0 ? 'text-foreground' : 'text-muted-foreground'}`}>
                        {conv.last_message_direction === 'outbound' && <span className="text-muted-foreground">You: </span>}
                        {conv.last_message_body}
                      </p>
                      {conv.unread > 0 && (
                        <span className="inline-block mt-1 px-1.5 py-0.5 rounded-full bg-primary text-white text-[10px] font-semibold">
                          {conv.unread} new
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        {/* Message thread */}
        <section className="bg-white border rounded-lg overflow-hidden flex flex-col">
          {!selected ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-2 p-8 text-center">
              <Phone size={32} />
              <p className="text-sm">Select a conversation</p>
            </div>
          ) : (
            <>
              <header className="border-b p-3 flex items-center gap-2">
                <Button variant="ghost" size="sm" className="md:hidden h-8 w-8 p-0" onClick={() => setSelected(null)}>
                  <ChevronLeft size={18} />
                </Button>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate" data-testid="sms-thread-header">
                    {selected.contact?.name || formatPhoneDisplay(selected.their_number)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatPhoneDisplay(selected.their_number)}
                    <span className="mx-1">·</span>
                    via {formatPhoneDisplay(selected.our_number)}
                  </p>
                </div>
              </header>

              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {loadingMsgs && messages.length === 0 ? (
                  <div className="flex items-center justify-center h-32">
                    <Loader2 className="animate-spin text-muted-foreground" />
                  </div>
                ) : messages.map(m => {
                  const isMe = m.direction === 'outbound';
                  return (
                    <div
                      key={m.id}
                      className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                      data-testid={`sms-msg-${m.id}`}
                    >
                      <div className={`max-w-[75%] rounded-2xl px-3 py-2 ${
                        isMe
                          ? 'bg-primary text-white rounded-br-sm'
                          : 'bg-muted text-foreground rounded-bl-sm'
                      }`}>
                        <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>
                        {m.media_urls?.length > 0 && (
                          <div className="mt-1 space-y-1">
                            {m.media_urls.map((url, i) => (
                              <img key={i} src={url} alt="MMS" className="rounded max-w-full" />
                            ))}
                          </div>
                        )}
                        <div className={`mt-1 text-[10px] ${isMe ? 'text-white/70' : 'text-muted-foreground'}`}>
                          {fullTimestamp(m.created_at)}
                          {isMe && m.sent_by_user_email && (
                            <> · {m.sent_by_user_email}</>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>

              <footer className="border-t p-3 bg-muted/20">
                <div className="flex gap-2 items-end">
                  <Textarea
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    placeholder="Type a message…"
                    className="min-h-[44px] resize-none text-sm"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendReply();
                      }
                    }}
                    data-testid="sms-draft"
                  />
                  <Button
                    onClick={sendReply}
                    disabled={sending || !draft.trim()}
                    className="gap-2 flex-shrink-0"
                    data-testid="sms-send-btn"
                  >
                    {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    Send
                  </Button>
                </div>
              </footer>
            </>
          )}
        </section>
      </div>

      {/* New SMS modal */}
      {composeOpen && (
        <div className="fixed inset-0 z-[9999] bg-black/50 flex items-end md:items-center justify-center p-0 md:p-6">
          <div className="bg-white w-full md:max-w-md md:rounded-xl shadow-2xl flex flex-col overflow-hidden">
            <header className="flex items-center justify-between px-4 py-3 border-b">
              <h2 className="text-base font-semibold">New SMS</h2>
              <Button variant="ghost" size="sm" onClick={() => setComposeOpen(false)} className="h-8 w-8 p-0">
                <X size={18} />
              </Button>
            </header>
            <div className="p-4 space-y-3">
              <div>
                <label className="text-xs text-muted-foreground">From</label>
                <select
                  value={composeFrom}
                  onChange={e => setComposeFrom(e.target.value)}
                  className="mt-1 w-full px-3 py-2 border rounded-md text-sm"
                  data-testid="new-sms-from"
                >
                  {numbers.map(n => (
                    <option key={n.sid} value={n.phone_number}>
                      {formatPhoneDisplay(n.phone_number)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">To</label>
                <Input
                  value={composeTo}
                  onChange={e => setComposeTo(e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  className="mt-1"
                  data-testid="new-sms-to"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Message</label>
                <Textarea
                  value={composeBody}
                  onChange={e => setComposeBody(e.target.value)}
                  placeholder="Type your message…"
                  className="mt-1 min-h-[100px] text-sm"
                  data-testid="new-sms-body"
                />
                <p className="text-[10px] text-muted-foreground mt-1">
                  {composeBody.length} character{composeBody.length === 1 ? '' : 's'}
                </p>
              </div>
            </div>
            <footer className="flex items-center justify-end gap-2 p-3 border-t bg-muted/20">
              <Button variant="ghost" size="sm" onClick={() => setComposeOpen(false)}>Cancel</Button>
              <Button onClick={startNewConversation} disabled={sending} className="gap-2" data-testid="new-sms-send-btn">
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                Send
              </Button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
